import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Copy, Check, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Homepage "Current Promotions" section.
 *
 * Reads active promos with `show_on_homepage=true` from
 * `/api/promotions?surface=homepage`. Renders nothing when the DB
 * returns zero eligible promotions (no hardcoded fallback).
 *
 * Placed on the home page after Featured Research Peptides and before
 * the Third-Party Testing section. Uses only approved RCM colors.
 */
const HomePromotions = () => {
  const [promos, setPromos] = useState([]);
  const [copiedId, setCopiedId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/promotions?surface=homepage`);
        if (!res.ok) return;
        const list = await res.json();
        if (!cancelled) setPromos(Array.isArray(list) ? list : []);
      } catch {
        // silent
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading || promos.length === 0) return null;

  const handleCopy = async (code, id) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(code);
      } else {
        const ta = document.createElement('textarea');
        ta.value = code;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1800);
    } catch {
      // ignore
    }
  };

  const formatExpires = (iso) => {
    if (!iso) return null;
    // Accept both proper ISO-8601 (`2026-08-06T04:59:00+00:00`) and
    // legacy admin-entered short forms (`8/6/26`, `8/6/2026`,
    // `2026-08-06`). The old code fed everything through `new Date()`
    // which returns Invalid Date for `8/6/26` in Safari (Chrome parses
    // it as 2026 or 1926 depending on version) — so the banner and
    // /promotions page silently rendered nothing for SAVE15.
    let d = new Date(iso);
    if (Number.isNaN(d.getTime())) {
      // Try M/D/YY or M/D/YYYY manually.
      const m = /^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/.exec(String(iso).trim());
      if (m) {
        const [, mo, day, yr] = m;
        const year = yr.length === 2 ? 2000 + Number(yr) : Number(yr);
        d = new Date(year, Number(mo) - 1, Number(day));
      }
    }
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  };

  return (
    <section
      className="section-padding rcm-section reveal"
      data-testid="home-current-promotions"
      style={{ background: '#020B14' }}
    >
      <div className="container">
        <div className="text-center mb-12">
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '10px',
              background: 'rgba(0, 174, 239, 0.08)',
              padding: '8px 20px',
              borderRadius: '24px',
              marginBottom: '16px',
              border: '1px solid rgba(0, 174, 239, 0.2)',
            }}
          >
            <Sparkles size={18} style={{ color: '#00AEEF' }} aria-hidden="true" />
            <span style={{ color: '#00AEEF', fontWeight: 600, fontSize: '14px', letterSpacing: '0.5px' }}>
              CURRENT PROMOTIONS
            </span>
          </div>
          <h2 className="hero-medium mb-4" style={{ color: '#00AEEF' }}>
            Save on Research Peptides
          </h2>
          <p className="body-large" style={{ color: '#C7D3DF', maxWidth: '620px', margin: '0 auto' }}>
            Live offers for qualifying research orders. Copy a code and apply it at checkout.
          </p>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: promos.length === 1
              ? 'minmax(280px, 640px)'
              : 'repeat(auto-fill, minmax(320px, 1fr))',
            justifyContent: 'center',
            gap: '24px',
            maxWidth: '1100px',
            margin: '0 auto',
          }}
        >
          {promos.map((p) => {
            const code = p.code || '';
            const isCopied = copiedId === p.id;
            const expires = formatExpires(p.schedule?.expires_at);
            const cta = p.content?.cta_text || 'Shop Qualifying Products';
            const dest = p.content?.cta_destination || '/products';

            return (
              <article
                key={p.id}
                data-testid={`home-promo-card-${p.code}`}
                style={{
                  background: '#071A33',
                  border: '1px solid rgba(0, 174, 239, 0.25)',
                  borderRadius: '14px',
                  padding: '28px',
                  display: 'flex',
                  flexDirection: 'column',
                  boxShadow: '0 4px 24px rgba(0, 87, 184, 0.15)',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                {/* subtle accent bar */}
                <div style={{
                  position: 'absolute', top: 0, left: 0, right: 0, height: '3px',
                  background: 'linear-gradient(90deg, #0057B8, #00AEEF)',
                }} />

                <h3 style={{
                  color: '#FFFFFF',
                  fontSize: '22px',
                  fontWeight: 700,
                  marginBottom: '10px',
                  lineHeight: 1.25,
                }}>
                  {p.content?.public_headline || p.name}
                </h3>
                <p style={{
                  color: '#C7D3DF',
                  fontSize: '14px',
                  lineHeight: 1.6,
                  marginBottom: '20px',
                  flex: 1,
                }}>
                  {p.content?.public_short_description || ''}
                </p>

                {/* Code block */}
                {code && (
                  <div
                    data-testid={`home-promo-code-block-${p.code}`}
                    style={{
                      display: 'flex',
                      alignItems: 'stretch',
                      gap: '0',
                      marginBottom: '16px',
                      border: '1px dashed rgba(199, 211, 223, 0.4)',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      background: 'rgba(0, 87, 184, 0.15)',
                    }}
                  >
                    <div style={{
                      flex: 1,
                      padding: '12px 14px',
                      color: '#FFFFFF',
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                      fontSize: '15px',
                      fontWeight: 700,
                      letterSpacing: '0.08em',
                      display: 'flex',
                      alignItems: 'center',
                    }}>
                      {code}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleCopy(code, p.id)}
                      data-testid={`home-promo-copy-${p.code}`}
                      aria-label={`Copy promo code ${code}`}
                      style={{
                        background: isCopied ? '#00AEEF' : '#0057B8',
                        color: '#FFFFFF',
                        border: 'none',
                        padding: '0 18px',
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontSize: '13px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        transition: 'background 0.15s ease',
                        letterSpacing: '0.03em',
                      }}
                    >
                      {isCopied ? <Check size={15} /> : <Copy size={15} />}
                      {isCopied ? 'Copied' : 'Copy Code'}
                    </button>
                  </div>
                )}

                {/* Meta row: expires + terms link */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '8px',
                  marginBottom: '16px',
                  fontSize: '12px',
                  color: '#C7D3DF',
                }}>
                  {expires && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                      <Clock size={13} aria-hidden="true" />
                      Ends {expires}
                    </span>
                  )}
                  <Link
                    to="/promotions"
                    style={{ color: '#00AEEF', textDecoration: 'none', fontWeight: 600 }}
                    data-testid={`home-promo-terms-${p.code}`}
                  >
                    Terms &amp; details →
                  </Link>
                </div>

                <Link
                  to={dest}
                  className="rcm-btn"
                  data-testid={`home-promo-cta-${p.code}`}
                  style={{
                    background: '#00AEEF',
                    color: '#020B14',
                    textAlign: 'center',
                    fontWeight: 700,
                    padding: '12px 20px',
                    borderRadius: '8px',
                    textDecoration: 'none',
                    display: 'inline-flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  {cta}
                  <ArrowRight size={16} />
                </Link>

                {p.content?.important_exclusions && (
                  <p style={{
                    marginTop: '12px',
                    fontSize: '11px',
                    color: 'rgba(199, 211, 223, 0.75)',
                    lineHeight: 1.5,
                    fontStyle: 'italic',
                  }}>
                    {p.content.important_exclusions}
                  </p>
                )}
              </article>
            );
          })}
        </div>

        <div className="text-center mt-8">
          <Link
            to="/promotions"
            data-testid="home-view-all-promos"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              color: '#00AEEF',
              fontWeight: 600,
              fontSize: '15px',
              textDecoration: 'none',
              padding: '10px 24px',
              border: '1px solid rgba(0, 174, 239, 0.4)',
              borderRadius: '8px',
            }}
          >
            View all promotions
            <ArrowRight size={18} />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default HomePromotions;
